import React, { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { defaultPagination } from "utils/table";
import { getUniformItemsApi, getWarehousesApi, getUniformSizesApi } from "api/services/uniformManagement";
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMediaQuery } from "react-responsive";

const uniformMgmtContext = createContext();

export const useUniformMgmt = () => {
  return useContext(uniformMgmtContext);
};

export const UniformMgmtProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const navigate = useNavigate();

  // Shared master data
  const [sizes, setSizes] = useState([]);
  const [sizesLoading, setSizesLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);

  // Active tab for the main page
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "items");

  const fetchSizes = useCallback(() => {
    setSizesLoading(true);
    getUniformSizesApi({}).then(response => {
      setSizes(response.data?.data || []);
      setSizesLoading(false);
    }).catch(() => setSizesLoading(false));
  }, []);

  const fetchWarehouses = useCallback(() => {
    setWarehousesLoading(true);
    getWarehousesApi({}).then(response => {
      setWarehouses(response.data?.data || []);
      setWarehousesLoading(false);
    }).catch(() => setWarehousesLoading(false));
  }, []);

  useEffect(() => {
    fetchSizes();
    fetchWarehouses();
  }, []);

  const sizeOptions = useMemo(() => {
    const sizeOrder = ["XS", "S", "M", "L", "XL", "XXL"];
    let sorted = [...sizes].sort((a, b) => sizeOrder.indexOf(a.code) - sizeOrder.indexOf(b.code));
    return sorted.map(item => ({ label: `${item.name} (${item.code})`, value: item.id }));
  }, [sizes]);

  const warehouseOptions = useMemo(() => {
    return warehouses.filter(w => w.status).map(item => ({ label: item.name, value: item.id }));
  }, [warehouses]);

  const value = useMemo(() => ({
    sizes, setSizes, sizesLoading, fetchSizes, sizeOptions,
    warehouses, setWarehouses, warehousesLoading, fetchWarehouses, warehouseOptions,
    activeTab, setActiveTab,
    isMobile, navigate
  }), [sizes, sizesLoading, warehouses, warehousesLoading, activeTab, isMobile, sizeOptions, warehouseOptions]);

  return (
    <uniformMgmtContext.Provider value={value}>
      {children}
    </uniformMgmtContext.Provider>
  );
};
